import { capitalize } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { translate } from "app/filters/i18nFilter";
import { EXPORT_INTEGRATION_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import { ManagementModal } from "app/pages/sales-intelligence/common-components/modals/ManagementModal/ManagementModal";
import { selectIsMissingMandatoryFieldsModalOpen } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/managementSelectors";
import { setIsMissingMandatoryFieldsModalOpen } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/modal";
import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import { setRecordToEnableExport } from "app/pages/sales-intelligence/sub-modules/management/store/actions/common";
import { selectRecordToEnableExport } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/common";
import { setIsExportRecordEnabledConfig } from "app/pages/sales-intelligence/sub-modules/management/store/actions/crmActionConfig";
import { modalStyles, StyledModalDetails, titleStyles, detailsStyles } from "./styles";
import { useMissingMandatoryFieldsModalTracking } from "./useMissingMandatoryFieldsModalTracking";
import { setTabNameThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/common";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { PayloadActionCreator } from "typesafe-actions";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

export const MissingMandatoryFieldsModal = () => {
    const dispatch = useDispatch();
    const isOpen = useSelector(selectIsMissingMandatoryFieldsModalOpen);
    const recordName = useSelector(selectRecordToEnableExport);
    const { integrationName } = getIntegrationNameFromUrl();

    const crmActionConfig = setIsExportRecordEnabledConfig[integrationName as Integration];
    const setIsExportRecordEnabled = (crmActionConfig as Record<ExportName, PayloadActionCreator<string, boolean>>)[
        recordName as ExportName
    ];

    const { trackConfirm, trackCancel } = useMissingMandatoryFieldsModalTracking(recordName);

    const handleConfirm = () => {
        dispatch(setIsMissingMandatoryFieldsModalOpen(false));
        dispatch(setTabNameThunk(recordName as ExportName));
        proNavigator.go(EXPORT_INTEGRATION_ROUTE[integrationName as Integration]);
        dispatch(setIsExportRecordEnabled(false));
        trackConfirm();
    };

    const handleClose = () => {
        dispatch(setIsExportRecordEnabled(false));
        dispatch(setIsMissingMandatoryFieldsModalOpen(false));
        dispatch(setRecordToEnableExport(null));
        trackCancel();
    };

    return (
        <ManagementModal
            customStyles={modalStyles}
            customTitleStyles={titleStyles}
            customDetailsStyles={detailsStyles}
            isOpen={isOpen}
            title={translate("si.management.manage-integration.missing-mandatory-fields-modal.title")}
            onConfirm={handleConfirm}
            onClose={handleClose}
            cancelBtnText="si.common.button.cancel"
            cancelBtnType="flat"
            confirmBtnText={translate("si.management.manage-integration.missing-mandatory-fields-modal.map-fields")}
            details={
                <>
                    <StyledModalDetails>
                        {translate("si.management.manage-integration.missing-mandatory-fields-modal.details-1", {
                            crmName: getCrmName(integrationName as Integration),
                            recordName:
                                recordName === "companyAsLeads"
                                    ? capitalize("leads")
                                    : capitalize(recordName as ExportName),
                        })}
                    </StyledModalDetails>
                    <StyledModalDetails>
                        {translate("si.management.manage-integration.missing-mandatory-fields-modal.details-2")}
                    </StyledModalDetails>
                </>
            }
        />
    );
};
